/**
 * <s-wishlist-list> — renders saved products inside the wishlist drawer.
 * Labels come from data-* attributes (translated in Liquid).
 */
import { getItems, remove, onChange } from '@theme/wishlist-store';

const safeUrl = (url) => (typeof url === 'string' && url.startsWith('/') && !url.startsWith('//') ? url : '');
const safeImage = (src) => (typeof src === 'string' && /^(https:)?\/\//.test(src) ? src : '');

class SWishlistList extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.list = this.querySelector('[data-wl-items]');
    this.empty = this.querySelector('[data-wl-empty]');
    if (!this.list) return;
    this.render();
    onChange(() => this.render(), signal);
    this.list.addEventListener('click', (event) => {
      const button = event.target.closest('[data-wl-remove]');
      if (button) remove(button.dataset.wlRemove);
      const addButton = event.target.closest('[data-wl-add]');
      if (addButton) this.#handleAdd(addButton);
    }, { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  render() {
    const items = getItems();
    if (this.empty) this.empty.hidden = items.length > 0;
    this.list.replaceChildren(...items.map((item) => this.#row(item)));
  }

  #row(item) {
    const url = safeUrl(item.url) || '#';
    const li = document.createElement('li');
    li.className = 'lachi-wl-row';

    const media = document.createElement('a');
    media.href = url;
    media.className = 'lachi-wl-media';
    media.tabIndex = -1;
    media.setAttribute('aria-hidden', 'true');
    const src = safeImage(item.image);
    if (src) {
      const img = document.createElement('img');
      img.src = src;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 72;
      img.height = 108;
      media.append(img);
    }

    const info = document.createElement('div');
    info.className = 'lachi-wl-info';
    const title = document.createElement('a');
    title.href = url;
    title.className = 'lachi-wl-title';
    title.textContent = item.title || '';
    const price = document.createElement('p');
    price.className = 'lachi-wl-price';
    price.textContent = item.price || '';
    if (item.compare && item.compare !== item.price) {
      const compare = document.createElement('s');
      compare.className = 'lachi-wl-compare';
      compare.textContent = item.compare;
      price.append(' ', compare);
    }
    const view = document.createElement('a');
    view.href = url;
    view.className = 'lachi-wl-view lachi-wl-view--secondary';
    view.textContent = this.dataset.viewLabel || 'View';

    // Size picker stays hidden until the product's options are loaded on first tap.
    const select = document.createElement('select');
    select.className = 'lachi-wl-select';
    select.hidden = true;
    select.dataset.wlSelect = '';
    select.setAttribute('aria-label', (this.dataset.optionLabel || 'Size for __TITLE__').replace('__TITLE__', item.title || ''));

    const addButton = document.createElement('button');
    addButton.type = 'button';
    addButton.className = 'lachi-wl-view lachi-wl-add';
    addButton.dataset.wlAdd = item.handle;
    addButton.dataset.wlUrl = url;
    addButton.textContent = this.dataset.addLabel || 'Add to cart';

    const actions = document.createElement('div');
    actions.className = 'lachi-wl-actions';
    actions.append(addButton, view);

    const message = document.createElement('p');
    message.className = 'lachi-wl-msg';
    message.dataset.wlMsg = '';
    message.setAttribute('role', 'status');

    info.append(title, price, select, actions, message);

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'lachi-wl-remove';
    removeButton.dataset.wlRemove = item.handle;
    removeButton.setAttribute('aria-label', `${this.dataset.removeLabel || 'Remove'}: ${item.title || ''}`);
    removeButton.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';

    li.append(media, info, removeButton);
    return li;
  }

  async #handleAdd(button) {
    const row = button.closest('.lachi-wl-row');
    const select = row?.querySelector('[data-wl-select]');
    const message = row?.querySelector('[data-wl-msg]');
    if (!row || !select || button.disabled) return;
    if (message) message.textContent = '';

    // First tap: load the product's sizes. One available option adds straight away;
    // several show the picker so the shopper chooses before adding.
    if (!select.dataset.loaded) {
      button.disabled = true;
      try {
        const variants = await this.#loadVariants(button.dataset.wlUrl);
        select.dataset.loaded = 'true';
        const available = variants.filter((v) => v.available);
        if (!available.length) {
          button.textContent = this.dataset.soldOutLabel || 'Sold out';
          return;
        }
        if (variants.length === 1) {
          await this.#addToCart(variants[0].id, button, message);
          return;
        }
        const placeholder = new Option(this.dataset.chooseLabel || 'Choose size', '');
        placeholder.disabled = true;
        placeholder.selected = true;
        select.replaceChildren(placeholder, ...variants.map((v) => {
          const label = v.available ? v.title : `${v.title} — ${this.dataset.soldOutLabel || 'Sold out'}`;
          const option = new Option(label, String(v.id));
          option.disabled = !v.available;
          return option;
        }));
        select.hidden = false;
        select.focus();
      } catch (error) {
        if (message) message.textContent = this.dataset.errorLabel || '';
      } finally {
        if (button.textContent !== (this.dataset.soldOutLabel || 'Sold out')) button.disabled = false;
      }
      return;
    }

    if (!select.value) {
      select.hidden = false;
      select.focus();
      if (message) message.textContent = this.dataset.chooseLabel || '';
      return;
    }
    await this.#addToCart(select.value, button, message);
  }

  async #loadVariants(url) {
    if (!url || url === '#') throw new Error('Missing product URL');
    const response = await fetch(`${url.split('?')[0]}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
    const product = await response.json();
    return Array.isArray(product.variants) ? product.variants : [];
  }

  async #addToCart(variantId, button, message) {
    const label = button.textContent;
    button.disabled = true;
    button.textContent = this.dataset.addingLabel || label;
    try {
      const result = await window.Shopify?.actions?.updateCart?.(
        { lines: [{ merchandiseId: String(variantId), quantity: 1 }] },
        { event: { context: 'wishlist' } }
      );
      // Cart errors (e.g. out of stock) are already shown as a toast by the shared cart handler.
      if (!result) {
        if (message) message.textContent = this.dataset.errorLabel || '';
        return;
      }
      if (result.userErrors?.length) return;
      this.closest('dialog')?.close();
      await window.Shopify?.actions?.openCart?.();
    } catch (error) {
      /* the shared cart handler has already shown the error toast */
    } finally {
      button.disabled = false;
      button.textContent = label;
    }
  }
}

if (!customElements.get('s-wishlist-list')) customElements.define('s-wishlist-list', SWishlistList);
